import {
	useDefaultSettingsQuery,
	useDeviceLogoQuery,
	useGetOrganizationQuery,
	useGetOrganizationSettingsQuery,
	useOrganizationActivitiesQuery,
	useOrganizationUsersQuery,
	usePortalLogoQuery,
} from '../../api';
import { organizationFormDefaultValues, settingsDefaultValues } from '../model/defaultValues';
import { mapSettingsToFormValues } from '../model/mappers';

import type { OrganizationFormValues } from '../model/types';

const getCombinedQueryState = (
	queries: { isFetching: boolean; isLoading: boolean; isError: boolean }[]
) => {
	return {
		isLoading: queries.some((query) => query.isFetching || query.isLoading),
		isError: queries.some((query) => query.isError),
	};
};

export const useOrganizationsDataQuery = (
	id = '',
	defaultValue = organizationFormDefaultValues
) => {
	const skip = !id;
	const organizationQuery = useGetOrganizationQuery(id, { skip });
	const settingsQuery = useGetOrganizationSettingsQuery(id, { skip });
	const usersQuery = useOrganizationUsersQuery(id, { skip });
	const activitiesQuery = useOrganizationActivitiesQuery(id, { skip });
	const defaultSettingsQuery = useDefaultSettingsQuery();
	const portalLogoQuery = usePortalLogoQuery(id, { skip });
	const deviceLogoQuery = useDeviceLogoQuery(id, { skip });

	if (defaultSettingsQuery.isLoading || defaultSettingsQuery.isFetching) {
		return {
			data: undefined,
			isLoading: true,
			isError: false,
		};
	}

	if (defaultSettingsQuery.isError) {
		return {
			data: undefined,
			isLoading: false,
			isError: true,
		};
	}

	const mappedDefaultSettings = defaultSettingsQuery.data
		? mapSettingsToFormValues(defaultSettingsQuery.data)
		: settingsDefaultValues;

	if (skip) {
		return {
			data: { ...defaultValue, settings: mappedDefaultSettings },
			isLoading: false,
			isError: false,
		};
	}

	const { isError, isLoading } = getCombinedQueryState([
		settingsQuery,
		organizationQuery,
		usersQuery,
		activitiesQuery,
		portalLogoQuery,
		deviceLogoQuery,
	]);

	if (isLoading) {
		return {
			data: undefined,
			isLoading: true,
			isError: false,
		};
	}

	if (isError) {
		return {
			data: undefined,
			isLoading: false,
			isError: true,
		};
	}

	const organization = organizationQuery.data;
	const settings = settingsQuery.data;
	const users = usersQuery.data;
	const activities = activitiesQuery.data;
	const mappedSettings = settings ? mapSettingsToFormValues(settings) : mappedDefaultSettings;
	const mappedSettingsWithLogos: OrganizationFormValues['settings'] = {
		...mappedSettings,
		watch: {
			...mappedSettings.watch,
			logo: deviceLogoQuery.data ?? '',
		},
	};

	const organizationFormValues: OrganizationFormValues = {
		general: {
			id: organization?.id,
			name: organization?.name ?? '',
			parentOrganization: organization?.parent_id ?? '',
			organizationType: (organization?.type ??
				'') as OrganizationFormValues['general']['organizationType'],
			timeZone: organization?.time_zone ?? '',
			city: organization?.street_address?.city ?? '',
			address: organization?.street_address?.street ?? '',
			country: organization?.street_address?.country ?? '',
			hostName: '',
			organizationLogo: portalLogoQuery.data ?? '',
		},
		users: {
			users:
				users?.map((user) => ({
					id: user.id,
					email: user.email,
					firstName: user.firstname,
					lastName: user.lastname,
					password: '',
					pin: '',
				})) ?? [],
		},
		settings: {
			...mappedSettingsWithLogos,
			shiftPlanner: {
				...mappedSettingsWithLogos.shiftPlanner,
				activities:
					activities?.map((activity) => ({
						serverId: activity.id,
						clientId: activity.id,
						name: activity.name,
						color: activity.color,
					})) ?? [],
			},
		},
	};

	return {
		data: organizationFormValues,
		isLoading: false,
		isError: false,
	};
};
